import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ImagePropType from './ImagePropType';

export const AcceptsWebpContext = React.createContext(false);

export const getEntryImgWidthHeight = (theme, isWide, isMobile, classes = []) => {
  const isFeaturedNews = theme === 'featured';
  const isOpinionTheme = theme === 'opinion';
  const isVoicesTheme = theme === 'voices';
  let widthHeight;
  if (classes.includes('ce-screenWidth--full')) {
    widthHeight = 'auto';
  } else if (isMobile) {
    widthHeight = 500;
  } else if (isFeaturedNews) {
    widthHeight = 1280;
  } else if (isWide || classes.includes('ce-screenWidth--wide')) {
    widthHeight = 970;
  } else if (isOpinionTheme) {
    widthHeight = 740;
  } else if (isVoicesTheme) {
    widthHeight = 736;
  } else {
    widthHeight = 720;
  }
  return widthHeight;
};

export const getEntryImgOps = (ops, index, theme) => {
  const isOpinionTheme = theme === 'opinion';
  const isVoicesTheme = theme === 'voices';
  let imageOps = ops;
  if (index === 0) {
    if (isOpinionTheme) {
      imageOps = ['736_510', 'quality_75'];
    } else if (isVoicesTheme) {
      imageOps = ['544_544', 'quality_75'];
    }
  }
  return imageOps;
};

const opImageSizeCalculators = {
  crop: {
    predicate: (op) => op.startsWith('crop'),
    calc: (size, op) => {
      const [height, width] = op.split('_').reverse();

      return {
        width: parseInt(width, 10),
        height: parseInt(height, 10),
      };
    },
  },
  resize: {
    predicate: (op) => op.startsWith('resize'),
    calc: (size, op) => {
      const [, width, height] = op.split('_');

      return {
        width: parseInt(width, 10),
        height: parseInt(height, 10),
      };
    },
  },
  scaleFit: {
    predicate: (op) => op.toLowerCase().startsWith('scalefit'),
    calc: (size, op) => {
      // Because the format supports only including one dimension.
      // I think maybe a regex and then pulling based on what that returns
      // might be better than this...
      const [, width, maybeHeight, maybeModifier] = op.split('_');
      const widthInt = parseInt(width, 10);
      const maybeHeightInt = parseInt(maybeHeight, 10);
      const heightInt = maybeHeightInt || widthInt;
      const modifier = !maybeHeightInt ? maybeHeight : maybeModifier;

      if (modifier === 'noupscale' && widthInt > size.width && heightInt > size.height) {
        return size;
      }

      const scaleBy = Math.min(widthInt, heightInt);
      const dimension = scaleBy === widthInt ? size.width : size.height;
      const scale = scaleBy / dimension;

      // Use Math.floor because hector rounds down.
      return {
        width: Math.floor(size.width * scale),
        height: Math.floor(size.height * scale),
      };
    },
  },
  numbersOnly: {
    predicate: (op) => op.match(/^\d+_\d+$/),
    calc: (size, op) => {
      const [width, height] = op.split('_');

      return {
        width: parseInt(width, 10),
        height: parseInt(height, 10),
      };
    },
  },
};

export const getImageSize = (image, ops = []) => ops.reduce((size, op) => {
  const calculator = Object.values(opImageSizeCalculators).find((c) => c.predicate(op));
  if (calculator) {
    return calculator.calc(size, op);
  }

  return size;
}, { width: image.width, height: image.height });

export const getQueryParams = ({ queryParams }, ops = []) => (
  { ...queryParams, ops: ops.join(',') }
);

export const getImageSrc = (image, ops = [], acceptsWebp = false) => {
  if (typeof image === 'string') {
    return image;
  }

  const { url } = image;

  const queryParams = getQueryParams(image, ops);
  let queryString = Object.keys(queryParams).reduce((accum, key) => {
    const value = queryParams[key];
    if (value) {
      accum.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
    return accum;
  }, []);
  if (acceptsWebp) {
    queryString.push('format=webp');
  }
  queryString = queryString.join('&');

  return `${url}${queryString ? `?${queryString}` : ''}`;
};

export const getImageSrcSet = (image, ops = [], acceptsWebp = false) => {
  if (typeof image === 'string') {
    return image;
  }

  const normalSrc = getImageSrc(image, ops, acceptsWebp);

  // Create hi-res version by doubling scalefit values
  const hiResOps = ops.map((op) => {
    if (op.toLowerCase().startsWith('scalefit')) {
      const parts = op.split('_');
      if (parts.length >= 2) {
        const width = parseInt(parts[1], 10);
        const height = parts.length >= 3 ? parseInt(parts[2], 10) : width;
        const modifier = parts.length >= 4 ? parts[3] : undefined;

        if (!Number.isNaN(width)) {
          const hiResWidth = width * 2;
          const hiResHeight = !Number.isNaN(height) ? height * 2 : hiResWidth;

          return [
            parts[0], // 'scalefit'
            hiResWidth,
            hiResHeight !== hiResWidth ? hiResHeight : undefined,
            modifier,
          ].filter(Boolean).join('_');
        }
      }
    }
    return op;
  });

  const hiResSrc = getImageSrc(image, hiResOps, acceptsWebp);

  return `${normalSrc} 1x, ${hiResSrc} 2x`;
};

const maybeGetImageOps = (args) => {
  const {
    width,
    height,
    operation,
    upscale = false,
    progressive = false,
    quality,
    ops = [],
    keepOriginalQuality, // this means we force quality_100 and NOT webp
  } = args;
  const finalQuality = keepOriginalQuality ? 100 : quality;
  const baseOps = [
    progressive && 'prog',
    finalQuality && `quality_${finalQuality}`,
  ].filter((o) => o);
  if (!width && !height) {
    return baseOps;
  }

  const opsAsString = (Array.isArray(ops) ? ops : ops.split(','))
    .filter((o) => o && o.indexOf('NaN') === -1)
    .join(',');

  return [
    [
      !operation && opsAsString.indexOf('crop_') !== -1 ? 'resize' : operation,
      width,
      // `scaleFit` supports only using a single dimension,
      // do that so its inline with Kraken to try and save hector cache.
      operation === 'scaleFit' && width === height ? undefined : height,
      !upscale && operation === 'scaleFit' ? 'noupscale' : undefined,
    ].filter((o) => o).join('_').toLowerCase(),
  ].concat(baseOps);
};

export const getOps = (args) => {
  const { ops = [] } = args;
  // Normalize and compact array.
  const splitOps = (Array.isArray(ops) ? ops : ops.split(','))
    .filter((o) => o && o.indexOf('NaN') === -1);

  const imageOps = maybeGetImageOps(args);

  return imageOps.length !== 0 ? splitOps.concat(imageOps) : splitOps;
};

function ampImageInvalidResponsive({ layout, width, height }) {
  return layout === 'responsive' && !width && !height;
}

function ampImageComponent(imageUrl, imageUrlWebp, imgProps, index, image, ops = []) {
  const sizeProps = { };

  if (ampImageInvalidResponsive(imgProps)) {
    // width and height are required with responsive layout
    if (imgProps.class.includes('landscape') || imageUrl.includes('scalefit_960_noupscale')) {
      sizeProps.width = 1.33;
      sizeProps.height = 1;
    } else if (imageUrl.includes('scalefit_686_noupscale')) {
      sizeProps.width = 1.5;
      sizeProps.height = 1;
    } else {
      sizeProps.width = 1;
      sizeProps.height = 1;
    }
    sizeProps.height = 1;
  }

  // https://amp.dev/documentation/guides-and-tutorials/optimize-and-measure/amp-optimizer-guide/explainer/?format=websites#hero-image-optimization
  // AMP Optimizers support a maximum of two hero images on a page
  // to avoid blocking bandwidth for other critical resources.
  const heroProps = index <= 2 ? { 'data-hero': true } : {};

  // Generate srcSet for hi-res displays in AMP
  const imageSrcSet = typeof image === 'string' ? undefined : getImageSrcSet(image, ops, false);
  const imageWebpSrcSet = typeof image === 'string' ? undefined : (imageUrlWebp && getImageSrcSet(image, ops, true));

  if (!imageUrlWebp) {
    return (
      <>
        <amp-img
          {...imgProps}
          {...sizeProps}
          {...heroProps}
          src={imageUrl}
          srcset={imageSrcSet}
          fallback=""
        />
        <span className="sr-only">Open Image Modal</span>
      </>
    );
  }
  return (
    <amp-img
      {...imgProps}
      {...sizeProps}
      {...heroProps}
      src={imageUrlWebp}
      srcset={imageWebpSrcSet || imageUrlWebp}
    >
      <amp-img
        {...imgProps}
        {...sizeProps}
        src={imageUrl}
        srcset={imageSrcSet}
        fallback=""
      />
      <span className="sr-only">Open Image Modal</span>
    </amp-img>
  );
}

function imageComponent(imageUrl, imageUrlWebp, props, ops, image) {
  /* eslint-disable jsx-a11y/alt-text */

  // Generate srcSet for hi-res displays
  const imageSrcSet = typeof image === 'string' ? imageUrl : getImageSrcSet(image, ops, false);
  const imageWebpSrcSet = typeof image === 'string' ? imageUrlWebp : (imageUrlWebp && getImageSrcSet(image, ops, true));

  return (
    <picture>
      {imageUrlWebp && <source type="image/webp" srcSet={imageWebpSrcSet || imageUrlWebp} />}
      <img
        {...props}
        src={imageUrl}
        srcSet={imageSrcSet}
      />
    </picture>
  );
  /* eslint-enable jsx-a11y/alt-text */
}

class Image extends PureComponent {
  render() {
    const {
      image,
      className,
      layout,
      alt,
      noWrapper,
      isAmp,
      role,
      ampLayout,
      lazy,
      fetchpriority,
      index,
      style = {},
      keepOriginalQuality,
    } = this.props;

    const loadingProp = lazy ? 'lazy' : undefined;

    if (!image) {
      return (
        <img
          className={classnames(['img', className])}
          src="https://img.huffingtonpost.com/asset/default-entry.jpg?ops=500_281"
          alt={alt}
          loading={loadingProp}
          fetchpriority={fetchpriority}
          style={style}
        />
      );
    }

    if (image.type === 'static' && !isAmp) {
      return (
        <img
          className={classnames(['img', className])}
          src={typeof image === 'string' ? image : getImageSrc(image, [], false)}
          alt={alt}
          loading={loadingProp}
          fetchpriority={fetchpriority}
          style={style}
        />
      );
    }

    const ops = getOps(this.props);
    const { width, height } = getImageSize(image, ops);
    const sized = !!(width && height);
    const orientation = width > height ? 'landscape' : 'portrait';

    const forceNoWrapper = isAmp || !sized || noWrapper;

    if (isAmp && !sized) {
      // wirepartner can display alt text otherwise no dims no dice
      if (className === 'entry-wirepartner__logo') {
        return <span className="amp-wirepartner-text">{alt.replace(' logo', '')}</span>;
      }
    }
    const useWebP = !keepOriginalQuality;
    const imageUrl = getImageSrc(image, ops, false);
    const imageUrlWebp = (imageUrl.indexOf('.gif') === -1) && getImageSrc(image, ops, useWebP);

    const imgClass = classnames({
      [className]: className && forceNoWrapper,
      img: forceNoWrapper,
      'img--responsive': layout === 'responsive' && forceNoWrapper,
      'img--inline': layout === 'inline' && forceNoWrapper,
      'img-sized__img': !forceNoWrapper,
      [orientation]: sized,
    });
    const commonImgProps = {
      alt,
      width: sized ? width : undefined,
      height: sized ? height : undefined,
    };
    const ampImgProps = {
      class: imgClass,
      layout: ampLayout,
      role: 'button',
      tabindex: '0',
      ...commonImgProps,
    };
    const imgProps = {
      style,
      className: imgClass,
      role: role || undefined,
      loading: loadingProp,
      fetchpriority,
      ...commonImgProps,
    };

    const renderedImage = isAmp
      ? ampImageComponent(imageUrl, imageUrlWebp, ampImgProps, index, image, ops)
      : imageComponent(imageUrl, imageUrlWebp, imgProps, ops, image);
    // I'm not positive if CAPI will ever send us a hector url without natural
    // image dimensions but this is just a safe gaurd in case.
    // UPDATE....IT DOES FOR WIREPARTNER LOGOS.
    if (forceNoWrapper) {
      return renderedImage;
    }

    const placeholderStyle = layout === 'max-width'
      ? { width }
      : undefined;

    return (
      <div
        className={classnames(className, {
          'img-sized': true,
          'img-sized--responsive': layout === 'responsive',
          'img-sized--inline': layout === 'inline',
        })}
      >
        <div className="img-sized__placeholder" style={placeholderStyle}>
          <div style={{ paddingTop: `${(height / width) * 100}%` }} />
        </div>
        {renderedImage}
      </div>
    );
  }
}

Image.contextType = AcceptsWebpContext;

Image.propTypes = {
  image: PropTypes.oneOfType([
    ImagePropType,
    PropTypes.shape({
      url: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          fileName: PropTypes.string.isRequired,
        }),
      ]).isRequired,
    }),
    PropTypes.string,
  ]).isRequired,
  index: PropTypes.number,
  layout: PropTypes.oneOf(['max-width', 'responsive', 'inline']),
  alt: PropTypes.string,
  className: PropTypes.string,
  noWrapper: PropTypes.bool,
  keepOriginalQuality: PropTypes.bool,
  isAmp: PropTypes.bool,
  ampLayout: PropTypes.string,
  role: PropTypes.string,
  lazy: PropTypes.bool,
  fetchpriority: PropTypes.string,
  style: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
};

Image.defaultProps = {
  className: undefined,
  index: undefined,
  layout: 'responsive',
  alt: '',
  keepOriginalQuality: false,
  noWrapper: false,
  isAmp: false,
  role: undefined,
  ampLayout: 'responsive',
  fetchpriority: 'auto',
  lazy: false,
  style: {},
};

export default Image;
